import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import ResponsiveContainer from './layout/ResponsiveContainer';
import SharedHeaderContractor from './SharedHeaderContractor';
// CB:BEGIN demo-bids-shared-import
import { setSelectedDemoProject } from '../lib/demoBids';
// CB:END demo-bids-shared-import
import { useUser, useAuth } from '@clerk/clerk-react';
import { useApi } from '../lib/useApi';
import { getApiUrl } from '../lib/getApiUrl';
import { useAuthFetch } from '../lib/useAuthFetch';
// CB:BEGIN contractor-role-guard
// Use dynamic import to avoid TDZ issues with UserTypeService
// import { UserTypeService } from '../lib/userTypeService';
// CB:END contractor-role-guard
// CB:BEGIN etag-contractor-import
// Use named import to avoid TDZ issues with default export
import { useETagResource } from '../hooks/useETagPolling';
// CB:END etag-contractor-import
import { useContractorReadiness } from '../hooks/useContractorReadiness';
import { useAppUser } from '../context/AppUserContext';
// CB:BEGIN extracted-components
import ContractorComplianceBanner from './contractor/ContractorComplianceBanner';
import AvailableProjectCard from './contractor/AvailableProjectCard';
import ActiveProjectCard from './contractor/ActiveProjectCard';
// CB:END extracted-components
// CB:BEGIN contractor-action-panel-import
import ContractorActionRequiredPanel from './ContractorActionRequiredPanel';
// CB:END contractor-action-panel-import
// CB:BEGIN contractor-availability-import
import AvailabilityToggle from './contractor/AvailabilityToggle';
// CB:END contractor-availability-import
// CB:BEGIN contractor-notification-preferences-import
import NotificationPreferencesPanel from './NotificationPreferencesPanel';
// CB:END contractor-notification-preferences-import

interface ContractorDashboardProps {
  onNavigate: (screen: string) => void;
  adminView?: boolean; // CB:BEGIN admin-view-flag
}

interface ContractorStatus {
  isVerified: boolean;
  verificationStatus: 'pending' | 'approved' | 'rejected' | 'not_submitted';
  stripeAccountId?: string; // CB:BEGIN stripe-status-tracking
  hasValidPayment?: boolean;
}

const ContractorDashboard: React.FC<ContractorDashboardProps> = ({ onNavigate, adminView = false }) => {
  const { user, isSignedIn } = useUser();
  const api = useApi();
  const authFetch = useAuthFetch();
  const [contractorStatus, setContractorStatus] = useState<ContractorStatus>({
    isVerified: false,
    verificationStatus: 'not_submitted',
    stripeAccountId: undefined // CB:END stripe-status-tracking
  });
  const [loading, setLoading] = useState(true);
  // CB:BEGIN payout-readiness-state
  const [payoutStatus, setPayoutStatus] = useState<{
    payoutReady: boolean;
    payoutReadyAt: string | null;
    stripeAccountId: string | null;
  } | null>(null);
  const [loadingPayout, setLoadingPayout] = useState(true);
  // CB:END payout-readiness-state
  // CB:BEGIN contractor-review-summary-state
  const [reviewAggregates, setReviewAggregates] = useState<{
    ratingAverage: number | null;
    reviewCount: number;
    averageCommunicationRating: number | null;
    averageProfessionalismRating: number | null;
    averageWorkmanshipRating: number | null;
    recommendationRate: number | null;
  } | null>(null);
  const [recentReviews, setRecentReviews] = useState<any[]>([]);
  // CB:END contractor-review-summary-state
  // CB:BEGIN dashboard-initial-loading
  const [initialLoading, setInitialLoading] = useState(true);
  // CB:END dashboard-initial-loading
  // CB:BEGIN contractor-role-validation
  const [roleValidated, setRoleValidated] = useState(false);
  const [roleCheckError, setRoleCheckError] = useState<string | null>(null);

  // CRITICAL: Validate user role from database before rendering dashboard
  useEffect(() => {
    // Skip validation if admin view (admin viewing contractor dashboard)
    if (adminView) {
      setRoleValidated(true);
      return;
    }

    if (!isSignedIn || !user) {
      return;
    }

    const validateRole = async () => {
      try {
        // Try to get role from UserTypeService first (has fallbacks)
        // Use dynamic import to avoid TDZ issues
        const { UserTypeService } = await import('../lib/userTypeService');
        const userType = await UserTypeService.getUserType(user);
        console.log('🔐 ContractorDashboard: UserTypeService returned:', userType);
        
        // If UserTypeService returns unknown, try direct API call
        if (userType === 'unknown') {
          try {
            const response = await fetch(getApiUrl('/api/me'), { credentials: 'include' });
            if (response.ok) {
              const contentType = response.headers.get('content-type');
              if (contentType && contentType.includes('application/json')) {
                const userData = await response.json();
                const role = String(userData?.role || '').toUpperCase();
                
                if (role === 'CONTRACTOR') {
                  localStorage.setItem('cb.usertype', 'contractor');
                  setRoleValidated(true);
                  return;
                } else if (role === 'HOMEOWNER') {
                  console.error('🚫 ContractorDashboard: Homeowner detected, redirecting to customer-dashboard');
                  localStorage.setItem('cb.usertype', 'homeowner');
                  onNavigate('customer-dashboard');
                  return;
                } else if (role === 'ADMIN') {
                  console.error('🚫 ContractorDashboard: Admin detected, redirecting to admin-dashboard');
                  localStorage.setItem('cb.usertype', 'admin');
                  onNavigate('admin-dashboard');
                  return;
                }
              }
            }
          } catch (apiError) {
            console.warn('⚠️ ContractorDashboard: API call failed, using UserTypeService result:', apiError);
          }
        }
        
        // Use UserTypeService result
        if (userType === 'contractor') {
          console.log('✅ ContractorDashboard: Role validated as CONTRACTOR');
          localStorage.setItem('cb.usertype', 'contractor');
          setRoleValidated(true);
          return;
        } else if (userType === 'homeowner') {
          console.error('🚫 ContractorDashboard: Homeowner detected, redirecting to customer-dashboard');
          localStorage.setItem('cb.usertype', 'homeowner');
          onNavigate('customer-dashboard');
          return;
        } else if (userType === 'admin') {
          console.error('🚫 ContractorDashboard: Admin detected, redirecting to admin-dashboard');
          localStorage.setItem('cb.usertype', 'admin');
          onNavigate('admin-dashboard');
          return;
        }
        
        // If still unknown, check cache as last resort
        const cached = localStorage.getItem('cb.usertype');
        if (cached === 'contractor') {
          console.log('✅ ContractorDashboard: Using cached contractor role');
          setRoleValidated(true);
          return;
        } else if (cached === 'homeowner') {
          console.error('🚫 ContractorDashboard: Cached homeowner, redirecting to customer-dashboard');
          onNavigate('customer-dashboard');
          return;
        } else if (cached === 'admin') {
          console.error('🚫 ContractorDashboard: Cached admin, redirecting to admin-dashboard');
          onNavigate('admin-dashboard');
          return;
        }
        
        // If we still don't know, allow access but log warning
        console.warn('⚠️ ContractorDashboard: Could not determine role, allowing access');
        setRoleValidated(true);
      } catch (error) {
        console.error('❌ ContractorDashboard: Error validating role:', error);
        // On error, check cache and allow if contractor
        const cached = localStorage.getItem('cb.usertype');
        if (cached === 'contractor') {
          console.log('✅ ContractorDashboard: Error occurred, using cached contractor role');
          setRoleValidated(true);
        } else {
          setRoleCheckError('Failed to verify access. Please try again.');
        }
      }
    };

    validateRole();
  }, [isSignedIn, user?.id, onNavigate, adminView]);
  // CB:END contractor-role-validation
  // CB:BEGIN readiness-hook
  const { refresh: refreshAppUser } = useAppUser();
  const {
    loading: rdLoading,
    stripeConnected,
    stripePayoutReady,
    stripeAccountId,
    verificationState,
    verificationStatusRaw,
    rejectionReason,
    accountReady,
    canSubmitBid,
    blockReason,
    bidCount,
    errorKind: readinessErrorKind,
  } = useContractorReadiness();
  useEffect(() => {
    try {
      localStorage.removeItem('contractor_demo_state');
      localStorage.removeItem('contractor_isVerified');
      localStorage.removeItem('contractor_payment_connected');
      localStorage.removeItem('isVerified');
    } catch {}
  }, []);
  // CB:END readiness-hook
  // CB:BEGIN available-projects-state
  const [availableProjects, setAvailableProjects] = useState<any[]>([]);
  // CB:END available-projects-state
  // CB:BEGIN realtor-quotes-state
  const [realtorQuotes, setRealtorQuotes] = useState<any[]>([]);
  // CB:END realtor-quotes-state
  // CB:BEGIN active-projects-state
  const [activeProjects, setActiveProjects] = useState<any[]>([]);
  const [loadingActiveProjects, setLoadingActiveProjects] = useState(true);
  // CB:END active-projects-state
  // CB:BEGIN bid-details-modal
  const [showBidDetailsModal, setShowBidDetailsModal] = useState(false);
  const [selectedProjectBid, setSelectedProjectBid] = useState<{ projectId: string; bid: any } | null>(null);
  // CB:BEGIN bid-quote-pdf-modal
  const [quotePdfBlobUrl, setQuotePdfBlobUrl] = useState<string | null>(null);
  const [quotePdfLoading, setQuotePdfLoading] = useState(false);
  const [quotePdfError, setQuotePdfError] = useState<string | null>(null);
  const { getToken } = useAuth();
  // CB:END bid-quote-pdf-modal
  // CB:END bid-details-modal

  // CB:BEGIN verification-submitted-popup
  const [showVerifiedPopup, setShowVerifiedPopup] = useState(() => {
    try {
      const flag = localStorage.getItem('contractor_verification_just_submitted');
      if (flag === '1') {
        localStorage.removeItem('contractor_verification_just_submitted');
        return true;
      }
    } catch {}
    return false;
  });
  // Refresh contractor data when returning from verification submission so banner reflects new status
  useEffect(() => {
    if (showVerifiedPopup) {
      refreshAppUser();
    }
  }, [showVerifiedPopup, refreshAppUser]);
  // CB:END verification-submitted-popup

  // FIX: Show demo banner if homeowner is allowed through
  useEffect(() => {
    if (sessionStorage.getItem('cb.demo.banner') === '1') {
      sessionStorage.removeItem('cb.demo.banner');
      // show a small banner/toast
      console.info('Contractor demo mode enabled');
    }
  }, []);

  // CB:BEGIN contractor-demo-flow
  // Demo-only state for single bid -> active project -> completed flow
  const [hasActiveProject, setHasActiveProject] = useState(false);
  const [phaseIndex, setPhaseIndex] = useState<number>(0); // 0: materials, 1: demolition, 2: completion
  const [showDepositModal, setShowDepositModal] = useState(false);
  const [depositAmount, setDepositAmount] = useState<number>(0);
  const [waitingForHomeownerIdx, setWaitingForHomeownerIdx] = useState<number | null>(null);
  const [completedDemoProject, setCompletedDemoProject] = useState<boolean>(false);
  const [bidSubmitted, setBidSubmitted] = useState<boolean>(false);
  const [bidAccepted, setBidAccepted] = useState<boolean>(false);
  const [showBidModal, setShowBidModal] = useState<boolean>(false);
  const [bidAmountInput, setBidAmountInput] = useState<string>('');
  const [showWinModal, setShowWinModal] = useState<boolean>(false);
  // CB:BEGIN contractor-draft-state
  const [bidDraftSaved, setBidDraftSaved] = useState<boolean>(false);
  // CB:END contractor-draft-state
  // CB:BEGIN platform-agreement-banner
  const [agreementRequiresAcceptance, setAgreementRequiresAcceptance] = useState<boolean>(false);
  useEffect(() => {
    if (adminView || !roleValidated || !isSignedIn) return;
    api.getPlatformAgreementStatus().then((data) => setAgreementRequiresAcceptance(!!data.requiresAcceptance)).catch(() => {});
  }, [adminView, roleValidated, isSignedIn]);
  // CB:END platform-agreement-banner
  // CB:BEGIN contractor-demo-acceptance
  const [acceptanceTimerStarted, setAcceptanceTimerStarted] = useState<boolean>(false);
  // CB:END contractor-demo-acceptance
  // CB:BEGIN contractor-local-load-guard
  const [loadedFromLocal, setLoadedFromLocal] = useState<boolean>(false);
  // CB:END contractor-local-load-guard
  // CB:BEGIN contractor-phase-completed-dates
  const [phaseCompletedAt, setPhaseCompletedAt] = useState<Record<number, number>>({});
  // CB:END contractor-phase-completed-dates

  // CB:BEGIN sendCongratsEmail-callback-early
  const sendCongratsEmail = useCallback(async (amountDisplay: string) => {
    try {
      const to = user?.primaryEmailAddress?.emailAddress || user?.emailAddresses?.[0]?.emailAddress;
      if (!to) return;
      const url = `${window.location.origin}/contractor-dashboard`;
      const subject = 'Congrats — Your bid was accepted!';
      const text = `Your bid (${amountDisplay}) was accepted. View your bid: ${url}`;
      const html = `
        <div style="font-family: Arial, sans-serif; max-width: 640px; margin: 0 auto;">
          <div style="background:#065f46;color:#fff;border-radius:12px 12px 0 0;padding:20px 16px;text-align:center;">
            <h2 style="margin:0;font-size:22px">Congratulations!</h2>
          </div>
          <div style="border:1px solid #e5e7eb;border-top:none;border-radius:0 0 12px 12px;padding:20px 16px;color:#111827;">
            <p style="margin:0 0 10px 0;">The homeowner accepted your bid for <strong>${demoAddress}</strong>.</p>
            <p style="margin:0 0 16px 0;">Bid Amount: <strong>${amountDisplay}</strong></p>
            <div style="text-align:center;margin-top:18px;">
              <a href="${url}" style="display:inline-block;background:#2563eb;color:#fff;text-decoration:none;padding:12px 18px;border-radius:8px;font-weight:700;">View Bid</a>
            </div>
          </div>
        </div>`;
      await fetch(getApiUrl('/api/email/send'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ to, subject, text, html })
      }).catch(() => {});
    } catch {}
  }, [user]);
  // CB:END sendCongratsEmail-callback-early

  const demoAddress = '1621 Crimson Hills Drive, St Peters MO 63376';
  const demoAmount = 18500; // total project amount for demo (legacy fallback)

  // Persist demo state
  useEffect(() => {
    try {
      // Fresh default unless contractor has interacted this session
      const interacted = sessionStorage.getItem('contractor_interacted') === '1';
      const saved = interacted ? JSON.parse(localStorage.getItem('contractor_demo_state') || 'null') : null;
      if (saved) {
        // FIX: Respect saved active/bidSubmitted state so Active Projects appears after submit
        const activeId = localStorage.getItem('active_project_id');
        let bidded: any = [];
        try { bidded = JSON.parse(localStorage.getItem('contractor_bidded_ids') || '[]'); } catch {}
        const shouldBeActive = !!activeId || !!(Array.isArray(bidded) && bidded.length) || !!saved.bidSubmitted || !!saved.hasActiveProject;

        setHasActiveProject(!!shouldBeActive);
        setPhaseIndex(typeof saved.phaseIndex === 'number' ? saved.phaseIndex : 0);
        setCompletedDemoProject(false);
        setBidSubmitted(!!saved.bidSubmitted);
        setBidAccepted(!!saved.bidAccepted);
        setBidAmountInput(String(saved.bidAmountInput || ''));
        setBidDraftSaved(!!saved.bidDraftSaved);
        // CB:BEGIN read-acceptance-intent
        const intend = typeof saved.intendAcceptedAt === 'number' ? saved.intendAcceptedAt : undefined;
        if (intend && intend > Date.now() && !saved.bidAccepted) {
          // schedule acceptance
          const delay = Math.max(0, intend - Date.now());
          if (!acceptanceTimerStarted) {
            setAcceptanceTimerStarted(true);
            setTimeout(async () => {
              setBidAccepted(true);
              setShowWinModal(true);
              // CB:BEGIN email-amount-fix
              let amountDisplay = '$0';
              try {
                const s = JSON.parse(localStorage.getItem('contractor_demo_state') || '{}');
                const raw = (typeof s?.bidAmountInput !== 'undefined' ? s.bidAmountInput : bidAmountInput) || '';
                const n = Number(String(raw).replace(/[^0-9.]/g, '')) || 0;
                // Customer pays bid + 10%
                const customer = Math.round(n * 1.10 * 100) / 100;
                amountDisplay = customer.toLocaleString(undefined, { style: 'currency', currency: 'USD' });
              } catch {}
              await sendCongratsEmail(amountDisplay);
              try {
                const s = JSON.parse(localStorage.getItem('contractor_demo_state') || '{}');
                s.bidAccepted = true;
                delete s.intendAcceptedAt;
                localStorage.setItem('contractor_demo_state', JSON.stringify(s));
              } catch {}
              // CB:END email-amount-fix
            }, delay);
          }
        }
        // CB:END read-acceptance-intent
        // CB:BEGIN read-phase-completed-dates
        try {
          if (saved.phaseCompletedAt && typeof saved.phaseCompletedAt === 'object') {
            setPhaseCompletedAt(saved.phaseCompletedAt as Record<number, number>);
          }
        } catch {}
        // CB:END read-phase-completed-dates
      } else {
        // Ensure a pristine demo state
        setHasActiveProject(false);
        setPhaseIndex(0);
        setCompletedDemoProject(false);
        setBidSubmitted(false);
        setBidAccepted(false);
        setBidAmountInput('');
        setBidDraftSaved(false);
        try { localStorage.removeItem('contractor_demo_state'); } catch {}
      }
      // Do not wipe existing draft/bid state here; preserve any saved values for prefill
      // Mark that initial local sync has completed
      setLoadedFromLocal(true);
    } catch {}
  }, [acceptanceTimerStarted, bidAmountInput, sendCongratsEmail]);
  useEffect(() => {
    // Avoid overwriting a freshly saved draft before we've loaded it
    if (!loadedFromLocal) return;
    try {
      const prev = JSON.parse(localStorage.getItem('contractor_demo_state') || '{}');
      const next = {
        ...prev,
        hasActiveProject,
        phaseIndex,
        completedDemoProject,
        bidSubmitted,
        bidAccepted,
        bidAmountInput,
        bidDraftSaved,
        // CB:BEGIN write-phase-completed-dates
        phaseCompletedAt,
        // CB:END write-phase-completed-dates
      };
      localStorage.setItem('contractor_demo_state', JSON.stringify(next));
    } catch {}
  }, [loadedFromLocal, hasActiveProject, phaseIndex, completedDemoProject, bidSubmitted, bidAccepted, bidAmountInput, bidDraftSaved, phaseCompletedAt]);

  // CB:BEGIN etag-contractor-poll
  // Poll active project for updates using ETag
  const activeProjectId = typeof window !== 'undefined' ? (localStorage.getItem('active_project_id') || 'demo-project') : 'demo-project';
  
  // CB:BEGIN skip-polling-if-demo
  // Skip ETag polling for demo projects to avoid 404s
  const shouldPoll = activeProjectId !== '0' && activeProjectId !== 'demo-project';
  // CB:END skip-polling-if-demo
  
  // CB:BEGIN disable-etag-when-no-active-projects
  // Disable ETag polling completely when there are no active projects to prevent unnecessary requests
  // Only enable polling if we actually have an active project AND it's not a demo project
  const shouldEnableETagPolling = shouldPoll && hasActiveProject && activeProjectId !== 'demo-project';
  // CB:END disable-etag-when-no-active-projects
  
  // CB:BEGIN debounce-refetch-handler
  // Debounce refetch to prevent infinite loops
  const refetchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const handleETagUpdate = useCallback(() => {
    // Only trigger if we actually have active projects
    if (!hasActiveProject) {
      return;
    }
    // Clear any pending refetch
    if (refetchTimeoutRef.current) {
      clearTimeout(refetchTimeoutRef.current);
    }
    // Debounce refetch by 5 seconds to prevent rapid-fire requests
    refetchTimeoutRef.current = setTimeout(() => {
      try { window.dispatchEvent(new CustomEvent('cb:refetch-active-projects')); } catch {}
    }, 5000);
  }, [hasActiveProject]);
  // CB:END debounce-refetch-handler
  
  // CB:BEGIN completely-disable-etag-when-no-active-projects
  // Only create ETag resource if we actually have active projects to prevent any fetches
  const { lastUpdated: _etagLastUpdated } = useETagResource<any>({
    url: shouldEnableETagPolling ? `/api/projects/${encodeURIComponent(activeProjectId)}` : '',
    interval: 60000, // CB:BEGIN increase-polling-interval - Increase to 60 seconds to reduce load
    // Only poll if we have active projects - this prevents ALL fetches when disabled
    enabled: shouldEnableETagPolling && hasActiveProject,
    onUpdate: handleETagUpdate
  });
  // CB:END completely-disable-etag-when-no-active-projects
  // CB:END etag-contractor-poll
  // CB:BEGIN db-draft-fetch
  const [dbDraftAmount, setDbDraftAmount] = useState<number | null>(null);
  useEffect(() => {
    (async () => {
      try {
        const projectId = localStorage.getItem('active_project_id') || 'demo-project';
        // CB:BEGIN skip-fetch-if-demo-or-invalid
        // Skip fetching if projectId is "0" or "demo-project" (not real projects)
        if (projectId === '0' || projectId === 'demo-project') return;
        // CB:END skip-fetch-if-demo-or-invalid
        
        // FIX: Use api.get so auth token is sent (avoids 403). Handle non-2xx gracefully.
        let j: { bids?: any[] } = { bids: [] };
        try {
          j = await api.get<{ bids: any[] }>(`/api/projects/${encodeURIComponent(projectId)}/bids`, { dedupeKey: false });
        } catch (err: any) {
          if (err?.message && !err.message.includes('403') && !err.message.includes('404')) {
            console.log(`⚠️ Unexpected error fetching bids for project ${projectId}:`, err?.message);
          }
          return;
        }
        const drafts = Array.isArray(j?.bids) ? j.bids.filter((b: any) => b?.status === 'DRAFT') : [];
        if (drafts.length > 0) {
          const latest = drafts[drafts.length - 1];
          const cents = typeof latest.amountCents === 'number' ? latest.amountCents : (typeof latest.amount === 'number' ? Math.round(latest.amount * 100) : 0);
          setDbDraftAmount(cents);
          setBidDraftSaved(true);
        }
      } catch (error) {
        // Silently handle network errors
        console.debug('Could not fetch bid drafts:', error);
      }
    })();
  }, []);
  // CB:END db-draft-fetch
  // CB:BEGIN demo-sqft-from-pdf
  const [demoSqft, setDemoSqft] = useState<number | null>(null);
  // CB:BEGIN removed-demo-pdf-fetch
  // Removed demo PDF fetching to eliminate 404 errors
  // CB:END removed-demo-pdf-fetch

  function activateProjectFromBid() {
    setHasActiveProject(true);
    setPhaseIndex(0);
  }

  function closeBidModal() { setShowBidModal(false); }

  async function handleSubmitBid() {
    // When setup is required, go straight to contractor verification (no alert)
    if (!canSubmitBid || !accountReady) {
      if (blockReason === 'verification_pending') {
        alert('Your verification is pending review. You can submit bids once an admin approves your profile.');
        return;
      }
      onNavigate('contractor-verification');
      return;
    }
    const raw = Number(bidAmountInput.replace(/[^0-9.]/g, '')) || 0;
    const clamped = Math.max(1000, Math.min(200000, raw));
    const cents = Math.round(clamped * 100);
    setBidAmountInput(clamped.toLocaleString(undefined, { style: 'currency', currency: 'USD' }));
    // Customer pays bid + 10% fee in demo
    const customerCents = Math.round((cents / 100) * 1.10 * 100);
    const amountDisplay = (customerCents / 100).toLocaleString(undefined, { style: 'currency', currency: 'USD' });
    setBidSubmitted(true);
    setShowBidModal(false);
    // Demo: accept immediately
    setTimeout(async () => {
      setBidAccepted(true);
      setShowWinModal(true);
      await sendCongratsEmail(amountDisplay);
    }, 400);
  }

  const { contractorSubmitPhase, getProjectPhases } = api;
  async function handlePhaseComplete(step: number) {
    if (step !== phaseIndex) return;
    // CB:BEGIN contractor-submit-phase
    try {
      const projectId = localStorage.getItem('active_project_id') || 'demo-project';
      await contractorSubmitPhase(projectId, step + 1);
      setWaitingForHomeownerIdx(step);

      // Poll phases for 4s to detect homeowner acceptance (demo auto-accept after 3s)
      const startedAt = Date.now();
      const poll = async () => {
        try {
          const res: any = await getProjectPhases(projectId);
          const phases = (res?.phases || []) as Array<{ number: number; homeownerAccepted?: boolean }>;
          const idx = phases.findIndex((p) => p.number === step + 1 && p.homeownerAccepted);
          if (idx !== -1) {
            // CB:BEGIN deposit-modal-on-accept
            setWaitingForHomeownerIdx(null);
            const frac = (step === 0 ? 0.25 : (step === 1 ? 0.25 : 0.5));
            // Base on accepted total (contractor bid + 10%)
            let base = 0;
            try {
              const raw = Number((bidAmountInput || '').replace(/[^0-9.]/g, '')) || 0;
              base = Math.round(raw * 1.10);
            } catch {}
            setDepositAmount(Math.round((base || 0) * frac));
            setShowDepositModal(true);
            // Advance happens after closing modal in closeDepositModal
            return;
            // CB:END deposit-modal-on-accept
          }
        } catch {}
        if (Date.now() - startedAt < 4500) {
          setTimeout(poll, 300);
        }
      };
      setTimeout(poll, 300);
    } catch {}
    // After submission, contractor waits for homeowner approval
    // CB:END contractor-submit-phase
  }

  function closeDepositModal() {
    setShowDepositModal(false);
    // CB:BEGIN mark-phase-completed-date
    try {
      const completedPhaseNumber = phaseIndex + 1;
      setPhaseCompletedAt((prev) => ({ ...prev, [completedPhaseNumber]: Date.now() }));
    } catch {}
    // CB:END mark-phase-completed-date
    if (phaseIndex < 2) {
      setPhaseIndex(phaseIndex + 1);
    } else {
      // Final phase → complete project
      setHasActiveProject(false);
      setCompletedDemoProject(true);
      setPhaseIndex(0);
    }
  }
  // CB:END contractor-demo-flow

  // FIX: Refresh lists after a bid submit (optimistic UX)
  useEffect(() => {
    const onBid = () => {
      try { window.dispatchEvent(new CustomEvent('cb:refetch-current-bids')); } catch {}
      try { window.dispatchEvent(new CustomEvent('cb:refetch-active-projects')); } catch {}
      // FIX: React to immediate bid submission
      setHasActiveProject(true);
      try {
        const state = JSON.parse(localStorage.getItem('contractor_demo_state') || '{}');
        state.hasActiveProject = true; state.bidSubmitted = true;
        localStorage.setItem('contractor_demo_state', JSON.stringify(state));
      } catch {}
    };
    window.addEventListener('cb:bid-submitted', onBid as any);
    return () => window.removeEventListener('cb:bid-submitted', onBid as any);
  }, []);

  // CB:BEGIN contractor-id-state
  // Store contractor ID to match bids
  const [contractorId, setContractorId] = useState<string | null>(null);
  useEffect(() => {
    (async () => {
      if (!user?.id) return;
      try {
        const response = await authFetch(getApiUrl('/api/contractors/self'));
        if (response.ok) {
          const data = await response.json();
          if (data?.contractor?.id) {
            setContractorId(data.contractor.id);
          }
        }
      } catch (error) {
        console.debug('Could not fetch contractor ID:', error);
      }
    })();
  }, [user?.id]);
  // CB:END contractor-id-state

  // CB:BEGIN fetch-contractor-reviews
  useEffect(() => {
    if (!isSignedIn || adminView) return;
    (async () => {
      try {
        const clerk = (window as any).Clerk;
        const token = clerk?.session ? await clerk.session.getToken() : null;
        if (!token) return;
        const response = await fetch(getApiUrl('/api/projects/reviews/my-reviews'), {
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        });
        if (response.ok) {
          const data = await response.json();
          if (data?.aggregates) setReviewAggregates(data.aggregates);
          if (data?.reviews) setRecentReviews(data.reviews.slice(0, 3));
        }
      } catch (err) {
        console.debug('Could not fetch contractor reviews:', err);
      }
    })();
  }, [isSignedIn, adminView]);
  // CB:END fetch-contractor-reviews

  // CB:BEGIN fetch-payout-status
  // Ref to latest api so effect only re-runs when isSignedIn/adminView change (not every render)
  const apiRef = useRef(api);
  apiRef.current = api;
  useEffect(() => {
    if (adminView || !isSignedIn) {
      setLoadingPayout(false);
      return;
    }

    let isMounted = true;
    (async () => {
      try {
        const status = await apiRef.current.getContractorPayoutStatus();
        if (isMounted) {
          setPayoutStatus(status);
        }
      } catch (error) {
        console.error('Error fetching payout status:', error);
        if (isMounted) {
          setPayoutStatus(null);
        }
      } finally {
        if (isMounted) {
          setLoadingPayout(false);
        }
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [isSignedIn, adminView]);
  // CB:END fetch-payout-status

  // CB:BEGIN fetch-active-projects
  // Fetch active projects (projects where contractor has accepted bids)
  const fetchActiveProjects = useCallback(async () => {
    if (!user?.id) {
      setLoadingActiveProjects(false);
      return;
    }
    
    try {
      setLoadingActiveProjects(true);
      const { getApiUrl } = await import('../lib/getApiUrl');
      const apiUrl = getApiUrl(`/api/contractors/active-projects?userId=${user.id}`);
      const response = await fetch(apiUrl, { credentials: 'include' });
      
      if (response.ok) {
        const data = await response.json();
        const projects = data.projects || [];
        setActiveProjects(projects);
        setHasActiveProject(projects.length > 0);
      }
    } catch (err) {
      console.error('Error fetching active projects:', err);
    } finally {
      setLoadingActiveProjects(false);
    }
  }, [user?.id]);
  
  // CB:BEGIN debounce-active-projects-refetch
  // Debounce refetch handler to prevent rapid-fire requests
  const refetchTimeoutRef2 = useRef<NodeJS.Timeout | null>(null);
  const lastFetchRef = useRef<number>(0);
  const fetchActiveProjectsRef = useRef(fetchActiveProjects);
  const MIN_FETCH_INTERVAL = 10000; // Only allow one fetch every 10 seconds
  
  // Keep ref updated with latest function
  useEffect(() => {
    fetchActiveProjectsRef.current = fetchActiveProjects;
  }, [fetchActiveProjects]);
  // CB:END debounce-active-projects-refetch
  
  useEffect(() => {
    // Only fetch if user is available
    if (!user?.id) {
      return;
    }
    
    // Initial fetch only
    lastFetchRef.current = Date.now();
    fetchActiveProjects();
    
    // Listen for refetch events (e.g., after bid acceptance)
    const eventHandler = () => {
      const now = Date.now();
      const timeSinceLastFetch = now - lastFetchRef.current;
      
      // If we just fetched recently, skip this refetch
      if (timeSinceLastFetch < MIN_FETCH_INTERVAL) {
        console.log('⏭️ Skipping active projects refetch - too soon since last fetch');
        return;
      }
      
      // Clear any pending refetch
      if (refetchTimeoutRef2.current) {
        clearTimeout(refetchTimeoutRef2.current);
      }
      // Debounce by 5 seconds to prevent rapid-fire requests
      refetchTimeoutRef2.current = setTimeout(() => {
        lastFetchRef.current = Date.now();
        fetchActiveProjectsRef.current(); // Use ref to avoid stale closure
      }, 5000);
    };
    
    window.addEventListener('cb:refetch-active-projects', eventHandler);
    
    return () => {
      window.removeEventListener('cb:refetch-active-projects', eventHandler);
      // Clean up timeout on unmount
      if (refetchTimeoutRef2.current) {
        clearTimeout(refetchTimeoutRef2.current);
      }
    };
  }, [user?.id]); // CB:BEGIN fix-dependency - Only depend on user.id to prevent constant re-runs
  // CB:END fetch-active-projects

  // CB:BEGIN fetch-available-projects
  // Fetch available projects for contractors to bid on
  const fetchAndFilterProjects = useCallback(async () => {
    if (!user?.id) return;
    try {
      console.log('🔍 Fetching available projects for contractor...');
      // CB:BEGIN fix-api-url-contractor-dashboard
      // Use getApiUrl helper for proper URL resolution
      const { getApiUrl } = await import('../lib/getApiUrl');
      const apiUrl = getApiUrl('/api/projects?status=BIDDING');
      console.log('🔗 Fetching from:', apiUrl);
      const response = await authFetch(apiUrl, {});
      // CB:END fix-api-url-contractor-dashboard
      if (response.ok) {
        const data = await response.json();
        // Backend returns array when status=BIDDING, but handle both formats
        const projects = Array.isArray(data) ? data : (data?.projects || []);
        console.log('✅ Available projects received:', projects.length);
        
        // CB:BEGIN filter-removed-projects-dashboard
        // Filter out removed projects from dashboard
        try {
          const removedIds = JSON.parse(localStorage.getItem('contractor_removed_project_ids') || '[]');
          const removedSet = new Set(removedIds);
          const filtered = (projects || [])
            // Remove any placeholder/TBD address projects from contractor view
            .filter((p: any) => !!p && p.address && p.address !== 'Address TBD' && p.address !== 'TBD')
            .filter((p: any) => !removedSet.has(String(p.id)));
          console.log(`📊 Filtered ${filtered.length} projects (removed ${projects.length - filtered.length} from dashboard)`);
          console.log(`📋 Projects:`, filtered.map((p: any) => ({ id: p.id, address: p.address, status: p.status })));
          setAvailableProjects(filtered);
        } catch {
          // If error parsing, use all projects
          setAvailableProjects(projects || []);
        }
        // CB:END filter-removed-projects-dashboard
      } else {
        console.error('❌ Failed to fetch projects:', response.status, response.statusText);
        const errorText = await response.text().catch(() => '');
        console.error('❌ Error response:', errorText.substring(0, 200));
        setAvailableProjects([]);
      }
    } catch (error) {
      console.error('❌ Error fetching available projects:', error);
      setAvailableProjects([]);
    }
  }, [user?.id]);
  
  useEffect(() => {
    fetchAndFilterProjects();
  }, [fetchAndFilterProjects]);

  // CB:BEGIN fetch-realtor-quotes
  const fetchRealtorQuotes = useCallback(async () => {
    if (!user?.id) return;
    try {
      const { getApiUrl } = await import('../lib/getApiUrl');
      const response = await authFetch(getApiUrl('/api/contractor/realtor-quotes'), {});
      if (response.ok) {
        const data = await response.json();
        setRealtorQuotes(Array.isArray(data?.requests) ? data.requests : []);
      }
    } catch {
      setRealtorQuotes([]);
    }
  }, [user?.id]);

  useEffect(() => {
    fetchRealtorQuotes();
  }, [fetchRealtorQuotes]);
  // CB:END fetch-realtor-quotes

  // CB:BEGIN refresh-on-storage-change
  // Refresh projects when localStorage changes (when projects are removed)
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'contractor_removed_project_ids') {
        console.log('🔄 Detected removed projects change, refreshing dashboard...');
        fetchAndFilterProjects();
      }
    };
    
    // Listen for storage events from other tabs/windows
    window.addEventListener('storage', handleStorageChange);
    
    // Also listen for custom events from same window (CurrentBids page)
    const handleCustomStorageChange = () => {
      console.log('🔄 Custom storage change detected, refreshing dashboard...');
      fetchAndFilterProjects();
    };
    
    window.addEventListener('cb:projects-removed', handleCustomStorageChange);

    // Also refresh when admin uploads a roof report (contractor-readiness-refresh fires after verification events)
    const handleReadinessRefresh = () => {
      fetchAndFilterProjects();
      refreshAppUser();
      // Retry after 2s in case of DB propagation delay
      setTimeout(() => refreshAppUser(), 2000);
    };
    window.addEventListener('contractor-readiness-refresh', handleReadinessRefresh);

    // Poll every 60s so roof report availability updates without a full page reload
    const pollInterval = setInterval(() => fetchAndFilterProjects(), 60000);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('cb:projects-removed', handleCustomStorageChange);
      window.removeEventListener('contractor-readiness-refresh', handleReadinessRefresh);
      clearInterval(pollInterval);
    };
  }, [fetchAndFilterProjects]);
  // CB:END refresh-on-storage-change
  // CB:END fetch-available-projects

  // Fetch contractor verification status
  // FIX: Prevent duplicate fetch in React 18 StrictMode dev
  const fetchedRef = useRef(false);

  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;

    (async () => {
      if (!user?.id) {
        setLoading(false);
        return;
      }
      try { /* Readiness handled by hook */ } finally { setLoading(false); }
    })();
  }, [user]);

  // CB:BEGIN contractor-state-sync
  // Keep local state in sync with localStorage changes from other screens (e.g., ProjectDetails Save Draft)
  useEffect(() => {
    const syncFromLocal = () => {
      try {
        const saved = JSON.parse(localStorage.getItem('contractor_demo_state') || 'null');
        if (!saved) return;
        setBidDraftSaved(!!saved.bidDraftSaved);
        setBidSubmitted(!!saved.bidSubmitted);
        setBidAccepted(!!saved.bidAccepted);
        if (typeof saved.bidAmountInput === 'string') setBidAmountInput(saved.bidAmountInput);
      } catch {}
    };
    // Initial sync and periodic refresh for a short while
    syncFromLocal();
    const interval = setInterval(syncFromLocal, 1000);
    return () => clearInterval(interval);
  }, []);
  // CB:END contractor-state-sync

  // Close any modals and navigate when payment succeeds
  useEffect(() => {
    const onPayment = (e: any) => {
      try { setShowDepositModal(false); } catch {}
      try { setShowBidModal(false); } catch {}
      if (onNavigate) {
        onNavigate('contractor-active-projects');
      }
    };
    window.addEventListener('cb:payment-succeeded' as any, onPayment as any);
    return () => window.removeEventListener('cb:payment-succeeded' as any, onPayment as any);
  }, [onNavigate]);

  // CB:BEGIN remove-unused-variables
  // const getVerificationBadge = () => { // Removed - unused
  // CB:END remove-unused-variables

  // CB:BEGIN dashboard-initial-loading-timer
  // Wait for readiness + payout data before showing the dashboard so the banner state is
  // correct on first render and never flashes from empty → populated.
  useEffect(() => {
    if (!rdLoading && !loadingPayout) {
      setInitialLoading(false);
    }
  }, [rdLoading, loadingPayout]);
  // CB:END dashboard-initial-loading-timer

  // CB:BEGIN unified-finish-setup-handler
  const handleFinishSetupClick = () => {
    if (!stripeConnected) {
      // Route to Stripe onboarding / financial setup
      startStripeOnboarding();
      return;
    }
    
    if (verificationState !== 'approved') {
      // Route to verification wizard (license + insurance)
      onNavigate('contractor-verification');
      return;
    }
    
    // Fallback: if somehow accountReady is false for another reason, go to general setup
    onNavigate('contractor-dashboard');
  };
  // CB:END unified-finish-setup-handler

  // CB:BEGIN contractor-onboarding-cta
  const startStripeOnboarding = async () => {
    try {
      const userId = user?.id;
      if (!userId) {
        console.error('No user ID available');
        return;
      }
      
      console.log('🔍 Starting Stripe onboarding for user:', userId);
      
      // CB:BEGIN deferred-account-creation-flow
      // Account will be created when user starts onboarding, not before
      // Just create the account link - account creation happens in the backend
      // Use useApi instead of direct fetch to ensure proper API base URL handling
      console.log('🔍 Creating account link (account will be created when onboarding starts)...');
      const linkData = await api.post('/api/connect/account-link', {
        // Don't pass accountId - let backend create it when onboarding starts
        userId: userId,
        email: user?.primaryEmailAddress?.emailAddress || user?.emailAddresses?.[0]?.emailAddress,
        returnUrl: window.location.origin + '/contractor-dashboard',
        refreshUrl: window.location.href
      });
      // CB:END deferred-account-creation-flow
      
      // CB:BEGIN handle-platform-profile-error-account-link
      // Check if this is the platform profile setup error
      if (linkData?.error === 'platform_profile_required' || linkData?.actionUrl) {
        const userMessage = linkData?.userMessage || linkData?.message || 'Stripe requires platform setup';
        const actionUrl = linkData?.actionUrl || 'https://dashboard.stripe.com/settings/connect/platform-profile';
        
        const shouldOpen = window.confirm(
          `${userMessage}\n\nThis is a one-time setup required by Stripe. Would you like to open the Stripe dashboard now?`
        );
        if (shouldOpen) {
          window.open(actionUrl, '_blank');
        }
        return;
      }
      // CB:END handle-platform-profile-error-account-link
      
      // Check for error in response
      if (linkData?.error || linkData?.details) {
        const errorMessage = linkData?.details || linkData?.error || 'Failed to create payment setup link. Please try again.';
        console.error('Failed to create account link:', errorMessage);
        alert(errorMessage);
        return;
      }
      
      console.log('✅ Account link created:', linkData);
      if (linkData?.accountCreated) {
        console.log('✅ Stripe account was created when you started onboarding');
      }
      
      if (linkData?.url) {
        console.log('🔍 Opening Stripe onboarding in new tab...');
        window.open(linkData.url, '_blank', 'noopener,noreferrer');

        // CB:BEGIN stripe-return-sync
        // When the user returns to this tab after completing Stripe onboarding,
        // sync the account status from Stripe → DB, then refresh the banner.
        let synced = false;
        const onFocus = async () => {
          if (synced) return;
          synced = true;
          window.removeEventListener('focus', onFocus);
          try {
            const token = await getToken();
            await fetch(getApiUrl('/api/connect/sync'), {
              method: 'GET',
              credentials: 'include',
              headers: token ? { Authorization: `Bearer ${token}` } : {},
            });
          } catch (_) {}
          await refreshAppUser();
        };
        window.addEventListener('focus', onFocus);
        // CB:END stripe-return-sync
      } else {
        console.error('No onboarding URL received');
        alert('Failed to get payment setup link. Please try again.');
      }
    } catch (e: any) {
      console.error('Stripe onboarding error:', e);
      const msg = e?.message || 'An error occurred while setting up payments. Please try again.';
      alert(msg);
    }
  };
  // CB:END contractor-onboarding-cta

  // CB:BEGIN role-validation-guard
  // CRITICAL: Never render dashboard until role is validated from database
  if (!adminView && isSignedIn && user && !roleValidated && !roleCheckError) {
    return (
      <ResponsiveContainer className="container">
        <SharedHeaderContractor onNavigate={onNavigate} showHelpButton />
        <div className="content" style={{ textAlign: 'center', padding: '40px 20px' }}>
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p style={{ marginTop: '16px', color: '#6b7280', fontSize: '16px' }}>Verifying access...</p>
        </div>
      </ResponsiveContainer>
    );
  }

  // If role check failed, show error
  if (!adminView && roleCheckError) {
    return (
      <ResponsiveContainer className="container">
        <SharedHeaderContractor onNavigate={onNavigate} showHelpButton />
        <div className="content" style={{ textAlign: 'center', padding: '40px 20px' }}>
          <div style={{ color: '#dc2626', fontSize: '16px', marginBottom: '16px' }}>{roleCheckError}</div>
          <button
            onClick={() => {
              setRoleCheckError(null);
              setRoleValidated(false);
            }}
            className="btn btn-primary"
          >
            Retry
          </button>
        </div>
      </ResponsiveContainer>
    );
  }

  // If user is signed in but role validation failed (wrong role), don't render
  // The useEffect will handle redirect
  if (!adminView && isSignedIn && user && !roleValidated) {
    return null;
  }
  // CB:END role-validation-guard

  // CB:BEGIN dashboard-initial-loading-screen
  if (initialLoading) {
    return (
      <ResponsiveContainer className="container">
        {!adminView && (<SharedHeaderContractor onNavigate={onNavigate} showHelpButton />)}
        <div className="content" style={{ textAlign: 'center', padding: '40px 20px' }}>
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p style={{ marginTop: '16px', color: '#6b7280', fontSize: '16px' }}>Loading dashboard</p>
        </div>
      </ResponsiveContainer>
    );
  }
  // CB:END dashboard-initial-loading-screen

  return (
    <ResponsiveContainer className="container">
      {!adminView && (<SharedHeaderContractor onNavigate={onNavigate} showHelpButton />)}

      {/* Content */}
      <div className="content">
        {/* CB:BEGIN single-verification-banner — Extracted to ContractorComplianceBanner component */}
        <ContractorComplianceBanner
          agreementRequiresAcceptance={agreementRequiresAcceptance}
          accountReady={accountReady}
          verificationStatusRaw={verificationStatusRaw}
          verificationState={verificationState}
          rejectionReason={rejectionReason}
          stripeConnected={stripeConnected}
          stripePayoutReady={stripePayoutReady}
          stripeAccountId={stripeAccountId}
          loading={rdLoading}
          errorKind={readinessErrorKind ?? undefined}
          adminView={adminView}
          onNavigate={onNavigate}
          onStartStripeOnboarding={startStripeOnboarding}
        />
        {/* CB:END single-verification-banner */}
        {/* CB:BEGIN contractor-bid-modal */}
        {!adminView && showBidModal && (
          <div role="dialog" aria-modal="true" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50 }}>
            <div className="card" style={{ background: 'white', borderRadius: 12, padding: 20, width: 'min(460px, 92vw)', boxShadow: '0 10px 30px rgba(0,0,0,0.2)' }}>
              <h3 style={{ marginTop: 0 }}>Submit Your Bid</h3>
              <p style={{ color: '#6b7280', marginTop: 4 }}>Bid Now</p>
              <div style={{ marginTop: 12 }}>
                <label style={{ display: 'block', fontSize: 13, color: '#374151', marginBottom: 6 }}>Bid Amount (USD)</label>
                <input className="form-control" placeholder="$0.00" value={bidAmountInput} onChange={(e) => setBidAmountInput(e.target.value)} onBlur={() => {
                  const raw = Number(bidAmountInput.replace(/[^0-9.]/g, '')) || 0;
                  if (!raw) return;
                  const clamped = Math.max(1000, Math.min(200000, raw));
                  setBidAmountInput(clamped.toLocaleString(undefined, { style: 'currency', currency: 'USD' }));
                }} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
                <button className="btn btn-secondary" onClick={closeBidModal}>Cancel</button>
                <button className="btn btn-primary" onClick={handleSubmitBid} disabled={!bidAmountInput}>Submit Bid</button>
              </div>
            </div>
          </div>
        )}
        {!adminView && showWinModal && (
          <div role="dialog" aria-modal="true" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50 }}>
            <div className="card" style={{ background: 'white', borderRadius: 12, padding: 20, width: 'min(420px, 92vw)', boxShadow: '0 10px 30px rgba(0,0,0,0.2)' }}>
              <h3 style={{ marginTop: 0 }}>You Won!</h3>
              <p style={{ color: '#374151' }}>The homeowner chose your bid for {demoAddress}.</p>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
                <button className="btn btn-primary" onClick={() => setShowWinModal(false)}>OK</button>
              </div>
            </div>
          </div>
        )}
        {/* CB:END contractor-bid-modal */}
        {/* CB:BEGIN contractor-deposit-lightbox */}
        {!adminView && showDepositModal && (
          <div role="dialog" aria-modal="true" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50 }}>
            <div className="card" style={{ background: 'white', borderRadius: 12, padding: 20, width: 'min(420px, 92vw)', boxShadow: '0 10px 30px rgba(0,0,0,0.2)' }}>
              <h3 style={{ marginTop: 0 }} aria-live="polite">Deposit Received</h3>
              <p style={{ color: '#374151' }} aria-live="polite">
                ${depositAmount.toLocaleString()} deposited into your account.
              </p>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
                <button className="btn btn-primary" onClick={closeDepositModal}>OK</button>
              </div>
            </div>
          </div>
        )}
        {/* CB:END contractor-deposit-lightbox */}

        {/* CB:BEGIN verification-submitted-popup */}
        {showVerifiedPopup && (
          <div role="dialog" aria-modal="true" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 60 }}>
            <div style={{ background: 'white', borderRadius: 16, padding: '36px 32px', width: 'min(440px, 92vw)', boxShadow: '0 20px 60px rgba(0,0,0,0.25)', textAlign: 'center' }}>
              <h2 style={{ margin: '0 0 12px 0', fontSize: 22, fontWeight: 700, color: '#111827' }}>
                Verification Submitted
              </h2>
              <p style={{ margin: '0 0 24px 0', fontSize: 15, color: '#6b7280', lineHeight: 1.6 }}>
                Your license and insurance documents are under review by the CoreBids team. You can browse and submit bids right now — we'll notify you once approved.
              </p>
              <button
                type="button"
                className="btn btn-primary"
                style={{ padding: '12px 32px', fontSize: 15, fontWeight: 600, borderRadius: 8 }}
                onClick={() => setShowVerifiedPopup(false)}
              >
                Start browsing bids
              </button>
            </div>
          </div>
        )}
        {/* CB:END verification-submitted-popup */}

        
        {/* CB:BEGIN v2-contractor-kpi-strip */}
        {!adminView && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
            gap: 12,
            marginBottom: 20,
          }}>
            {[
              {
                label: 'Available Leads',
                value: availableProjects.length,
                sub: 'in your service area',
                color: '#2563eb',
                onClick: () => { const el = document.getElementById('available-projects-section'); if (el) el.scrollIntoView({ behavior: 'smooth' }); },
              },
              {
                label: 'Active Jobs',
                value: activeProjects.length,
                sub: 'currently in progress',
                color: '#059669',
                onClick: () => { const el = document.getElementById('active-projects-section'); if (el) el.scrollIntoView({ behavior: 'smooth' }); },
              },
              {
                label: 'Stripe Connected',
                value: stripeConnected ? 'Yes' : 'No',
                sub: stripeConnected ? 'Account linked' : 'Setup needed',
                color: stripeConnected ? '#059669' : '#d97706',
                onClick: undefined,
              },
              {
                label: 'Account Status',
                value: verificationState === 'approved' ? 'Verified' : 'Pending',
                sub: verificationState === 'approved' ? 'Licensed & insured' : 'Complete setup',
                color: verificationState === 'approved' ? '#059669' : '#d97706',
                onClick: undefined,
              },
            ].map(kpi => (
              <div
                key={kpi.label}
                onClick={kpi.onClick}
                style={{
                  background: 'white',
                  border: '1px solid #e5e7eb',
                  borderRadius: 10,
                  padding: '14px 16px',
                  cursor: kpi.onClick ? 'pointer' : 'default',
                  transition: 'box-shadow 0.15s',
                }}
                onMouseEnter={kpi.onClick ? e => { (e.currentTarget as HTMLElement).style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)'; } : undefined}
                onMouseLeave={kpi.onClick ? e => { (e.currentTarget as HTMLElement).style.boxShadow = 'none'; } : undefined}
              >
                <div style={{ fontSize: 22, fontWeight: 700, color: kpi.color, lineHeight: 1.1, marginBottom: 2 }}>
                  {kpi.value}
                </div>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 2 }}>{kpi.label}</div>
                <div style={{ fontSize: 11, color: '#9ca3af' }}>{kpi.sub}</div>
              </div>
            ))}
          </div>
        )}
        {/* CB:END v2-contractor-kpi-strip */}

        {/* CB:BEGIN contractor-today-section */}
        {!adminView && (
          <div style={{ marginBottom: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
              <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#111827' }}>Today's Actions</h2>
              <span style={{ fontSize: 12, color: '#6b7280', fontWeight: 400 }}>
                {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
              </span>
            </div>
            <p style={{ margin: '0 0 12px 0', fontSize: 13, color: '#6b7280' }}>
              Items requiring your attention to keep projects moving.
            </p>
          </div>
        )}
        {/* CB:END contractor-today-section */}

        {/* CB:BEGIN contractor-action-required-panel */}
        {!adminView && <ContractorActionRequiredPanel onNavigate={onNavigate} />}
        {/* CB:END contractor-action-required-panel */}

        {/* CB:BEGIN contractor-availability-toggle */}
        {!adminView && <AvailabilityToggle />}
        {/* CB:END contractor-availability-toggle */}

        {/* CB:BEGIN contractor-review-summary-card */}
        {reviewAggregates && reviewAggregates.reviewCount > 0 && (
          <div className="dashboard-card" style={{ marginBottom: 20, padding: 20 }}>
            <h3 style={{ margin: '0 0 16px 0', fontSize: 16 }}>Your Reviews</h3>
            <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'flex-start' }}>
              {/* Overall rating */}
              <div style={{ textAlign: 'center', minWidth: 100 }}>
                <div style={{ fontSize: 36, fontWeight: 700, color: '#1d4ed8', lineHeight: 1 }}>
                  {reviewAggregates.ratingAverage?.toFixed(1) ?? '—'}
                </div>
                <div style={{ fontSize: 18, color: '#f59e0b', letterSpacing: 2, margin: '4px 0' }}>
                  {'★'.repeat(Math.round(reviewAggregates.ratingAverage ?? 0))}
                  {'☆'.repeat(5 - Math.round(reviewAggregates.ratingAverage ?? 0))}
                </div>
                <div style={{ fontSize: 12, color: '#6b7280' }}>
                  {reviewAggregates.reviewCount} review{reviewAggregates.reviewCount !== 1 ? 's' : ''}
                </div>
              </div>
              {/* Category breakdowns */}
              <div style={{ flex: 1, minWidth: 200 }}>
                {[
                  { label: 'Communication', value: reviewAggregates.averageCommunicationRating },
                  { label: 'Professionalism', value: reviewAggregates.averageProfessionalismRating },
                  { label: 'Workmanship', value: reviewAggregates.averageWorkmanshipRating },
                ].map(({ label, value }) => (
                  <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                    <span style={{ fontSize: 13, color: '#374151', width: 120 }}>{label}</span>
                    <div style={{ flex: 1, background: '#e5e7eb', borderRadius: 4, height: 8, overflow: 'hidden' }}>
                      <div style={{ width: `${((value ?? 0) / 5) * 100}%`, background: '#1d4ed8', height: '100%', borderRadius: 4 }} />
                    </div>
                    <span style={{ fontSize: 13, color: '#6b7280', width: 28, textAlign: 'right' }}>{value?.toFixed(1) ?? '—'}</span>
                  </div>
                ))}
                {reviewAggregates.recommendationRate != null && (
                  <div style={{ fontSize: 13, color: '#059669', marginTop: 8, fontWeight: 500 }}>
                    {Math.round(reviewAggregates.recommendationRate)}% would recommend you
                  </div>
                )}
              </div>
            </div>
            {/* Recent review snippets */}
            {recentReviews.length > 0 && (
              <div style={{ marginTop: 16, borderTop: '1px solid #e5e7eb', paddingTop: 12 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 8 }}>Recent Feedback</div>
                {recentReviews.map((r: any) => (
                  <div key={r.id} style={{ marginBottom: 8, padding: '8px 12px', background: '#f9fafb', borderRadius: 8 }}>
                    {r.publicReviewText && (
                      <p style={{ fontSize: 13, color: '#374151', margin: '0 0 4px 0', fontStyle: 'italic' }}>
                        &ldquo;{r.publicReviewText.length > 120 ? r.publicReviewText.slice(0, 120) + '...' : r.publicReviewText}&rdquo;
                      </p>
                    )}
                    <div style={{ fontSize: 11, color: '#9ca3af' }}>
                      {'★'.repeat(r.overallContractorRating ?? 0)} · {r.project?.address || 'Project'} · {r.submittedAt ? new Date(r.submittedAt).toLocaleDateString() : ''}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        {/* CB:END contractor-review-summary-card */}

        {/* CB:END contractor-payment-info-section - Removed: now part of unified banner */}

        <div className="dashboard-grid" id="available-projects-section">
          {/* Current Bids */}
          <div className="dashboard-card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ margin: 0 }}>Current Bids</h3>
              <button 
                className="btn btn-secondary"
                style={{ color: 'black', width: 'fit-content', padding: '4px 8px', whiteSpace: 'nowrap' }}
                onClick={() => onNavigate('current-bids')}
              >
                View All
              </button>
            </div>
            {/* CB:BEGIN render-available-projects */}
            {/* CB:BEGIN realtor-quote-cards */}
            {realtorQuotes.slice(0, 3).map((q) => {
              const address = [q.propertyAddressLine1, q.propertyCity, q.propertyState].filter(Boolean).join(', ');
              const slotsLeft = (q.maxBids ?? 3) - (q.bidCount ?? 0);
              const alreadyBid = q.myBidStatus != null;
              return (
                <div
                  key={q.id}
                  className="project-card"
                  style={{
                    marginBottom: '16px',
                    padding: '20px',
                    border: alreadyBid ? '2px solid #7c3aed' : '1px solid #ddd6fe',
                    borderRadius: '8px',
                    background: alreadyBid ? '#faf5ff' : 'white',
                    boxShadow: alreadyBid
                      ? '0 2px 8px rgba(124, 58, 237, 0.15)'
                      : '0 1px 3px rgba(0,0,0,0.08)',
                    transition: 'all 0.2s ease',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = '#7c3aed';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(124, 58, 237, 0.18)';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = alreadyBid ? '#7c3aed' : '#ddd6fe';
                    e.currentTarget.style.boxShadow = alreadyBid ? '0 2px 8px rgba(124,58,237,0.15)' : '0 1px 3px rgba(0,0,0,0.08)';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ flex: 1 }}>
                      <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '4px', color: alreadyBid ? '#6d28d9' : '#7c3aed' }}>
                        {address || 'Address on file'}
                      </h3>
                      {q.distanceMiles != null && (
                        <p style={{ color: '#6b7280', fontSize: '13px', marginBottom: '4px' }}>
                          ({q.distanceMiles.toFixed(1)} miles from you)
                        </p>
                      )}
                      <p style={{ color: '#6b7280', marginBottom: '8px' }}>
                        {q.expiresAt ? `Expires ${new Date(q.expiresAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}` : 'Realtor Quote Request'}
                      </p>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' as const, marginBottom: 8 }}>
                        <span style={{
                          display: 'inline-block',
                          background: '#ede9fe',
                          color: '#6d28d9',
                          fontSize: 11,
                          fontWeight: 700,
                          padding: '3px 10px',
                          borderRadius: 20,
                          letterSpacing: '0.04em',
                          textTransform: 'uppercase' as const,
                        }}>
                          Realtor Quote
                        </span>
                        <span style={{
                          display: 'inline-block',
                          background: slotsLeft <= 1 ? '#fee2e2' : '#f0fdf4',
                          color: slotsLeft <= 1 ? '#dc2626' : '#166534',
                          fontSize: 11,
                          fontWeight: 600,
                          padding: '3px 10px',
                          borderRadius: 20,
                        }}>
                          {slotsLeft} slot{slotsLeft !== 1 ? 's' : ''} left
                        </span>
                      </div>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 12, flexWrap: 'wrap' as const }}>
                        <button
                          onClick={() => onNavigate(`/contractor/realtor-quotes/${q.id}`)}
                          style={{
                            height: 36,
                            padding: '0 18px',
                            borderRadius: 8,
                            border: 'none',
                            fontSize: 14,
                            fontWeight: 600,
                            cursor: 'pointer',
                            backgroundColor: alreadyBid ? '#7c3aed' : '#7c3aed',
                            color: 'white',
                            whiteSpace: 'nowrap' as const,
                          }}
                        >
                          {alreadyBid ? 'Review Bid' : 'Submit Bid'}
                        </button>
                        {q.hasRoofReport && (
                          <button
                            onClick={async (e) => {
                              e.stopPropagation();
                              try {
                                const { getApiUrl } = await import('../lib/getApiUrl');
                                const resp = await authFetch(getApiUrl(`/api/contractor/realtor-quotes/${q.id}/roof-report/download?format=json`));
                                if (!resp.ok) { alert('Failed to load roof report'); return; }
                                const data = await resp.json();
                                if (data.downloadUrl) { window.open(data.downloadUrl, '_blank', 'noopener,noreferrer'); }
                                else { alert('Report not available'); }
                              } catch {
                                alert('Failed to load roof report');
                              }
                            }}
                            style={{
                              height: 36,
                              padding: '0 18px',
                              borderRadius: 8,
                              border: '1.5px solid #10b981',
                              fontSize: 14,
                              fontWeight: 600,
                              cursor: 'pointer',
                              backgroundColor: 'white',
                              color: '#059669',
                              whiteSpace: 'nowrap' as const,
                            }}
                          >
                            View Report
                          </button>
                        )}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right', marginLeft: '16px', flexShrink: 0 }}>
                      {alreadyBid ? (
                        <span style={{
                          display: 'inline-block',
                          background: '#7c3aed',
                          color: 'white',
                          fontSize: '12px',
                          fontWeight: '700',
                          padding: '4px 10px',
                          borderRadius: '20px',
                          whiteSpace: 'nowrap',
                          letterSpacing: '0.03em',
                        }}>
                          ✓ BID SUBMITTED
                        </span>
                      ) : (
                        <span style={{
                          display: 'inline-block',
                          background: '#ede9fe',
                          color: '#6d28d9',
                          fontSize: '12px',
                          fontWeight: '700',
                          padding: '4px 10px',
                          borderRadius: '20px',
                          whiteSpace: 'nowrap',
                        }}>
                          {q.bidCount ?? 0} BID{(q.bidCount ?? 0) !== 1 ? 'S' : ''}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
            {/* CB:END realtor-quote-cards */}
            {availableProjects.length > 0 ? (
              availableProjects.slice(0, 3).map((project) => {
                // Check if contractor has bidded on this project
                let biddedIds: string[] = [];
                try {
                  biddedIds = JSON.parse(localStorage.getItem('contractor_bidded_ids') || '[]');
                } catch {}
                const localStorageBidded = Array.isArray(biddedIds) && biddedIds.includes(String(project.id));

                const currentEmail = user?.primaryEmailAddress?.emailAddress || user?.emailAddresses?.[0]?.emailAddress;
                const ownBidExists = Array.isArray(project.bids) && project.bids.some((bid: any) => {
                  if (contractorId && bid.contractorId) return bid.contractorId === contractorId;
                  if (currentEmail) return bid?.contractor?.user?.email === currentEmail;
                  return false;
                });
                const hasBidsInDb = (project.bids?.length || 0) > 0;
                const hasBidded = ownBidExists || (localStorageBidded && hasBidsInDb && !!contractorId);

                const hasRoofReport = !!(project.roofReportUrl || project.roofReportRedactedKey || project.roofReportUploadedAt);
                const isMissingReport = !hasRoofReport;

                // Handler for when contractor clicks "Submit Bid"
                const handleSubmitBidForProject = (projectId: string) => {
                  if (!accountReady) {
                    onNavigate('contractor-verification');
                    return;
                  }
                  localStorage.setItem('active_project_id', String(projectId));
                  onNavigate(`/contractor/projects/${String(projectId)}`);
                };

                // CB:BEGIN bid-quote-pdf-handler
                // Handler for when contractor clicks "Review Bid" — shows quote PDF
                const handleShowBidDetails = async (projectId: string, _bid: any) => {
                  // Always fetch from API so we get the contractor's own bid (not an arbitrary bid from project list)
                  let contractorBid: any = null;
                  try {
                    const data = await api.get<{ bids?: any[] }>(`/api/projects/${projectId}/bids`, { dedupeKey: false });
                    const bids = Array.isArray(data?.bids) ? data.bids : (Array.isArray(data) ? data : []);
                    if (contractorId) {
                      contractorBid = bids.find((b: any) => b.contractorId === contractorId);
                    }
                    if (!contractorBid) {
                      const currentEmail = user?.primaryEmailAddress?.emailAddress || user?.emailAddresses?.[0]?.emailAddress;
                      if (currentEmail) {
                        contractorBid = bids.find((b: any) =>
                          b.contractor?.user?.email === currentEmail
                        );
                      }
                    }
                    if (!contractorBid?.id) {
                      alert('Could not find your bid details. Please refresh the page.');
                      return;
                    }

                    setSelectedProjectBid({ projectId, bid: contractorBid });
                    setShowBidDetailsModal(true);
                    setQuotePdfBlobUrl(null);
                    setQuotePdfError(null);
                    setQuotePdfLoading(true);

                    const token = await getToken();
                    const resp = await fetch(getApiUrl(`/api/projects/${projectId}/bids/${contractorBid.id}/quote-pdf`), {
                      headers: token ? { Authorization: `Bearer ${token}` } : {},
                      credentials: 'include',
                    });
                    if (!resp.ok) {
                      const txt = await resp.text().catch(() => '');
                      setQuotePdfError(txt || `Failed to load PDF (${resp.status})`);
                      setQuotePdfLoading(false);
                      return;
                    }
                    const blob = await resp.blob();
                    setQuotePdfBlobUrl(URL.createObjectURL(blob));
                  } catch (error: any) {
                    console.error('Error loading bid quote PDF:', error);
                    setQuotePdfError(error?.message || 'Could not load PDF');
                  } finally {
                    setQuotePdfLoading(false);
                  }
                };
                // CB:END bid-quote-pdf-handler

                return (
                  <AvailableProjectCard
                    key={project.id}
                    project={project}
                    hasBidded={hasBidded}
                    isMissingReport={isMissingReport}
                    hasRoofReport={hasRoofReport}
                    adminView={adminView}
                    onSubmitBid={handleSubmitBidForProject}
                    onShowBidDetails={handleShowBidDetails}
                  />
                );
              })
            ) : (
              /* CB:END render-available-projects */
              /* Show empty state when no projects AND no realtor quotes available */
              realtorQuotes.length === 0 && (
                <div style={{ textAlign: 'center', padding: '40px 20px', color: '#6b7280' }}>
                  <p style={{ margin: 0, fontSize: '16px' }}>No open projects available</p>
                  <p style={{ margin: '8px 0 0 0', fontSize: '14px' }}>Check back later for new projects</p>
                </div>
              )
            )}
          </div>

          {/* Active Projects */}
          <div className="dashboard-card" id="active-projects-section">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ margin: 0 }}>Active Projects</h3>
              {hasActiveProject && (
                <button
                  className="btn btn-secondary"
                  style={{ color: 'black', width: 'fit-content', padding: '4px 8px', whiteSpace: 'nowrap' }}
                  onClick={() => onNavigate('contractor-active-projects')}
                >
                  View All
                </button>
              )}
            </div>
            {/* CB:BEGIN display-active-projects */}
            {loadingActiveProjects ? (
              <p style={{ color: '#6b7280', textAlign: 'center', padding: '40px 20px' }}>Loading...</p>
            ) : activeProjects.filter((p: any) => p.status !== 'COMPLETED').length === 0 ? (
              <p style={{ color: '#6b7280', textAlign: 'center', padding: '40px 20px' }}>
                No active projects. Submit a bid and get accepted to see your active projects here.
              </p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {activeProjects.filter((p: any) => p.status !== 'COMPLETED').slice(0, 3).map((project: any) => (
                  <ActiveProjectCard
                    key={project.id}
                    project={project}
                    onNavigate={onNavigate}
                  />
                ))}
              </div>
            )}
            {/* CB:END display-active-projects */}
          </div>

          {/* Completed Jobs */}
          <div className="dashboard-card">
            <h3>Completed Jobs</h3>
            {(() => {
              const completedProjects = activeProjects.filter((p: any) => p.status === 'COMPLETED');
              if (loadingActiveProjects) return <p style={{ color: '#6b7280', textAlign: 'center', padding: '40px 20px' }}>Loading...</p>;
              if (completedProjects.length === 0) return <div style={{ color: '#6b7280', fontSize: 14, textAlign: 'center', padding: '40px 20px' }}>No completed jobs yet</div>;
              return (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {completedProjects.map((project: any) => (
                    <ActiveProjectCard key={project.id} project={project} onNavigate={onNavigate} />
                  ))}
                </div>
              );
            })()}
          </div>
        </div>
      </div>
      
      {/* CB:BEGIN bid-details-modal */}
      {showBidDetailsModal && selectedProjectBid && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
          onClick={() => {
            setShowBidDetailsModal(false);
            if (quotePdfBlobUrl) { URL.revokeObjectURL(quotePdfBlobUrl); setQuotePdfBlobUrl(null); }
          }}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '20px',
              width: '90%',
              maxWidth: '860px',
              height: '88vh',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 20px 25px -5px rgba(0,0,0,0.15)',
              position: 'relative',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header row */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h2 style={{ margin: 0, color: '#1e40af', fontSize: '20px', fontWeight: '600' }}>
                Your Submitted Quote
              </h2>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                {quotePdfBlobUrl && (
                  <a
                    href={quotePdfBlobUrl}
                    download={`quote-${selectedProjectBid.bid.id?.slice(-8) ?? 'bid'}.pdf`}
                    style={{
                      backgroundColor: '#7c3aed',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      padding: '8px 16px',
                      fontSize: '14px',
                      fontWeight: '500',
                      cursor: 'pointer',
                      textDecoration: 'none',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                    }}
                  >
                    ↓ Download PDF
                  </a>
                )}
                <button
                  type="button"
                  onClick={() => {
                    setShowBidDetailsModal(false);
                    if (quotePdfBlobUrl) { URL.revokeObjectURL(quotePdfBlobUrl); setQuotePdfBlobUrl(null); }
                  }}
                  style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#6b7280', padding: '4px 8px', borderRadius: '4px', lineHeight: 1 }}
                  aria-label="Close"
                >
                  ×
                </button>
              </div>
            </div>

            {/* PDF viewer area */}
            <div style={{ flex: 1, minHeight: 0, borderRadius: '8px', overflow: 'hidden', border: '1px solid #e5e7eb', backgroundColor: '#f9fafb', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {quotePdfLoading && (
                <div style={{ textAlign: 'center', color: '#6b7280' }}>
                  <div style={{ fontSize: '14px' }}>Loading your quote PDF…</div>
                </div>
              )}
              {quotePdfError && !quotePdfLoading && (
                <div style={{ textAlign: 'center', padding: 24 }}>
                  <p style={{ color: '#dc2626', marginBottom: 8, fontSize: '14px' }}>{quotePdfError}</p>
                  <p style={{ color: '#6b7280', fontSize: '13px' }}>The quote PDF is only available for bids submitted via the structured quote form.</p>
                </div>
              )}
              {quotePdfBlobUrl && !quotePdfLoading && (
                <iframe
                  src={quotePdfBlobUrl}
                  style={{ width: '100%', height: '100%', border: 'none' }}
                  title="Your submitted quote"
                />
              )}
            </div>
          </div>
        </div>
      )}
      {/* CB:END bid-details-modal */}

      {/* CB:BEGIN contractor-notification-preferences */}
      {!adminView && (
        <div className="content" style={{ paddingTop: 0 }}>
          <NotificationPreferencesPanel role="CONTRACTOR" />
        </div>
      )}
      {/* CB:END contractor-notification-preferences */}
    </ResponsiveContainer>
  );
};

export default ContractorDashboard; 
